export const MAX_FORUM_IMAGES = 6;
export const MAX_FORUM_IMAGE_BYTES = 500 * 1024;
export const MAX_FORUM_SOURCE_BYTES = 10 * 1024 * 1024;
export const MAX_FORUM_IMAGE_PIXELS = 16000000;

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const JPEG_QUALITIES = [0.9, 0.75, 0.6, 0.45];

function imageError(translationKey) {
  const error = new Error(translationKey);
  error.translationKey = translationKey;
  return error;
}

async function hasImageSignature(file) {
  const header = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = reader.onabort = () => reject(imageError('forum.error.invalidImage'));
    reader.readAsArrayBuffer(file.slice(0, 12));
  });
  if (file.type === 'image/jpeg') {
    return header[0] === 0xff && header[1] === 0xd8 && header[2] === 0xff;
  }
  if (file.type === 'image/png') {
    return [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]
      .every((byte, index) => header[index] === byte);
  }
  return header.length >= 12
    && [0x52, 0x49, 0x46, 0x46].every((byte, index) => header[index] === byte)
    && [0x57, 0x45, 0x42, 0x50].every((byte, index) => header[index + 8] === byte);
}

export async function prepareForumImage(file) {
  if (!file || !ALLOWED_TYPES.has(file.type) || file.size === 0) {
    throw imageError('forum.error.invalidImage');
  }
  if (file.size > MAX_FORUM_SOURCE_BYTES) {
    throw imageError('forum.error.imageSourceLarge');
  }
  if (!await hasImageSignature(file)) {
    throw imageError('forum.error.invalidImage');
  }

  const image = new Image();
  let objectUrl;
  let canvas;
  try {
    objectUrl = URL.createObjectURL(file);
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = () => reject(imageError('forum.error.invalidImage'));
      image.src = objectUrl;
    });
    const { naturalWidth: width, naturalHeight: height } = image;
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      throw imageError('forum.error.invalidImage');
    }
    if (width * height > MAX_FORUM_IMAGE_PIXELS) {
      throw imageError('forum.error.imageDimensions');
    }

    const scale = Math.min(1, 1920 / Math.max(width, height));
    let targetWidth = Math.max(1, Math.round(width * scale));
    let targetHeight = Math.max(1, Math.round(height * scale));
    canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) throw imageError('forum.error.imageCompression');

    // Re-encode every image to remove metadata and flatten transparency.
    for (let resize = 0; resize < 5; resize += 1) {
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, targetWidth, targetHeight);
      context.drawImage(image, 0, 0, targetWidth, targetHeight);
      for (const quality of JPEG_QUALITIES) {
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob(
            result => result ? resolve(result) : reject(imageError('forum.error.imageCompression')),
            'image/jpeg',
            quality
          );
        });
        if (blob.type !== 'image/jpeg' || blob.size === 0) {
          throw imageError('forum.error.imageCompression');
        }
        if (blob.size <= MAX_FORUM_IMAGE_BYTES) {
          return new File([blob], 'image.jpg', { type: 'image/jpeg' });
        }
      }
      targetWidth = Math.max(1, Math.round(targetWidth * 0.75));
      targetHeight = Math.max(1, Math.round(targetHeight * 0.75));
    }
    throw imageError('forum.error.imageCompression');
  } catch (error) {
    if (error.translationKey) throw error;
    throw imageError('forum.error.imageCompression');
  } finally {
    image.onload = null;
    image.onerror = null;
    image.src = '';
    if (objectUrl) URL.revokeObjectURL(objectUrl);
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
